import { useEffect, useMemo } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import Divider from '@zenbusiness/zui/Divider';
import Grid from '@zenbusiness/zui/Grid';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { isChaseBank } from '#client/components/MoveMoney/utils/common';
import useCounterpartyDetails from '#client/hooks/useCounterpartyDetails';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useToastContext from '#client/hooks/useToastContext';
import { INSTITUTION_QUERY } from '#client/queries';

import AccountHolder from './AccountHolder';
import AccountNumber from './AccountNumber';
import Bank from './Bank';

export default function UserDetail({
  accountHolder,
  routingNumber,
  accountNumber,
  title,
  accountHolderTitle = 'Account holder',
  bank,
  counterpartyIdFromTransaction,
  fullAccountNumber,
  counterparty
}) {
  const { createCommonErrorToast } = useToastContext();
  const routingNumberParam = routingNumber ? routingNumber.trim() : '';
  const hasCounterPartyId = counterpartyIdFromTransaction || counterparty?.counterpartyId;

  const {
    data: originalBankData,
    error,
    errors,
    loading: loadingBank
  } = useQuery(INSTITUTION_QUERY, {
    variables: {
      params: {
        routingNumber: routingNumberParam
      }
    },
    skip: !routingNumber || hasCounterPartyId,
    skipCache: true,
    noThrow: true
  });

  useLogError(errors, error);
  const bankData = useMemoObject(originalBankData);

  // Get CounterParty Detail from transaction/payment if there is no existed counterParty is passed
  const { counterPartyData: counterPartyDataFromTransaction, fetchingCounterparty } =
    useCounterpartyDetails({
      counterpartyId: counterparty?.counterpartyId ? null : counterpartyIdFromTransaction
    });

  const fetchingData = fetchingCounterparty || loadingBank;

  // Check the bank from counterParty props or transaction/payment is Chase Bank or not
  const hideEyeBtn = isChaseBank({
    bank: counterparty?.bank || counterPartyDataFromTransaction?.bank,
    connectedType: counterparty?.connectedType || counterPartyDataFromTransaction?.connectedType
  });

  const bankText = useMemo(() => {
    // If the bank is constant text, return it
    if (bank) {
      return bank;
    }

    // Return the bank from counterParty passed from the page
    if (counterparty?.bank) {
      return counterparty?.bank;
    }

    // Return the bank the from transaction/payment
    if (counterPartyDataFromTransaction?.bank) {
      return counterPartyDataFromTransaction?.bank;
    }

    return bankData?.institution?.name || '';
  }, [
    bank,
    bankData?.institution?.name,
    counterPartyDataFromTransaction?.bank,
    counterparty?.bank
  ]);

  const accountNumberText = useMemo(() => {
    // Show the account number from counter party passed or transaction when hiding eye button
    if (hideEyeBtn) {
      return counterparty?.accountNumber || counterPartyDataFromTransaction?.accountNumber;
    }

    return accountNumber;
  }, [
    accountNumber,
    counterPartyDataFromTransaction?.accountNumber,
    counterparty?.accountNumber,
    hideEyeBtn
  ]);

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (!accountHolder && !routingNumber && !accountNumber) {
    return null;
  }

  return (
    <>
      <Divider />
      <Grid
        columnSpacing="8px"
        container
        pb={{ xs: '24px', md: '56px' }}
        pt={{ xs: '24px', md: '32px' }}
        px={{ xs: '24px', md: '32px' }}
        rowSpacing={{ xs: '16px', md: '32px' }}
      >
        <Grid
          item
          xs={12}
        >
          <Stack justifyContent="space-between">
            <Text
              color="primary"
              size="xl"
              weight="medium"
            >
              {title}
            </Text>
          </Stack>
        </Grid>
        <AccountHolder
          accountHolder={accountHolder}
          title={accountHolderTitle}
        />
        <Bank
          bank={bankText}
          loading={fetchingData}
        />
        <AccountNumber
          accountNumber={accountNumberText}
          fullAccountNumber={hideEyeBtn ? '' : fullAccountNumber}
          loading={hasCounterPartyId ? fetchingData : false}
        />
      </Grid>
    </>
  );
}

UserDetail.propTypes = {
  accountHolder: PropTypes.string,
  routingNumber: PropTypes.string,
  accountNumber: PropTypes.string,
  accountHolderTitle: PropTypes.string,
  bank: PropTypes.string,
  title: PropTypes.string.isRequired,
  counterpartyIdFromTransaction: PropTypes.string,
  fullAccountNumber: PropTypes.string,
  counterparty: PropTypes.shape({
    counterpartyId: PropTypes.string,
    bank: PropTypes.string,
    connectedType: PropTypes.string,
    accountNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string
  })
};
